/**
 * derivedEngine.ts — VIREON Cortex Derived Variable Computation Engine
 * =====================================================================
 * Processes raw + meta telemetry rows (sorted chronologically) and computes
 * ALL derived variables as specified in the VIREON-CORTEX Variables & Formulas
 * Reference PDF.
 *
 * Tariff: TGSPDCL HT Consumer, Category 1A, 33 kV.
 * Energy billing uses kVAh (apparent energy), not kWh.
 * Three-tier rate structure based on Max Demand vs Contract Demand ratio.
 *
 * Architecture:
 *   computeAllDerived(rows) → TelemetryRow[]
 *
 * The engine maintains a mutable EngineState across rows and for each row
 * emits a fully-populated TelemetryRow with all 67+ derived fields.
 *
 * Reset logic:
 *   • Daily accumulators  → reset when YYYY-MM-DD changes (midnight)
 *   • Monthly accumulators → reset when YYYY-MM changes (billing start = 1st of month)
 *   • Session accumulators → reset when Pi uptime drops (Pi reboot detected)
 *   • 15-min demand window → advances every 15 clock-aligned minutes
 *
 * PDF reference sections:
 *   §2.3 DERIVED sheet columns A–BO (67 fields)
 *   §3   TGSPDCL Tariff Parameters (Rs/kVAh, 3 tiers)
 *   §4   Full Derived Variable Reference
 */

import type { TelemetryRow, RawMeterRow, MetaRow } from '@/types';
import { percentile } from '@/lib/calculations';
import type { StateTariff, ToDPeriod } from '@/tariffs';
import { getTariff } from '@/tariffs';

/**
 * The derived engine is now STATE-AWARE. Every dataset is processed with the
 * tariff config for the meter's state (src/tariffs/<state>.ts), so:
 *   • ToD bucketing uses the state's todPeriodFor() windows
 *     (WBSEDCL 06–17 Normal / 17–23 Peak / 23–06 Off-Peak,
 *      TGSPDCL 06–10 & 18–22 Peak / 10–18 Normal / 22–06 Off-Peak)
 *   • Energy charges use the state's register (kWh for WB, kVAh for TG) + rates
 *   • Demand / ED / fixed charges follow the state's model
 * The tariff is passed in by sheetsClient (resolved from source.stateCode).
 */

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Safe number — returns fallback if n is not a finite number. */
const s = (n: unknown, fallback = 0): number =>
  typeof n === 'number' && Number.isFinite(n) ? n : fallback;

/** Clamp a value to [lo, hi]. */
const clamp = (v: number, lo: number, hi: number): number =>
  Math.max(lo, Math.min(hi, v));

/**
 * Canonical instantaneous apparent power VA_Total for a row.
 *   VA_Total = Watts_Total / PF_Avg          (per the variables spec)
 * PF_Avg is cleaned exactly as cleanPF() does: the meter's |PF_Avg| is used
 * when it is a sane (0,1] value, otherwise it is reconstructed from the sheet's
 * VA_Total column. Only when PF cannot be resolved at all do we fall back to
 * the raw VA_Total column. This makes the LK4405 sheets — whose native
 * VA_Total column is unreliable — compute apparent power from Watts/PF instead.
 *
 * WB-ONLY CHANGE (isKvah === false, i.e. WBSEDCL / kWh tariff):
 *   The raw VA_Total fallback is removed entirely. Apparent power is always
 *   Watts_Total / PF_Avg; if PF_Avg itself is invalid/unavailable, PF is
 *   assumed to be 1 (VA = Watts) rather than reading the meter's raw
 *   VA_Total register. Telangana (isKvah === true) behaviour is unchanged —
 *   it still falls back to the raw VA_Total column exactly as before.
 */
function apparentVa(row: RawMeterRow & MetaRow, isKvah: boolean): number {
  const watts = s(row.wattsTotal);
  const va    = s(row.vaTotal);
  const rawPf = Math.abs(s(row.truePFAvg));

  if (!isKvah) {
    // WB: never use raw VA_Total. PF_Avg valid → Watts/PF_Avg; otherwise
    // assume unity PF (VA = Watts).
    const pf = (rawPf > 0 && rawPf <= 1) ? rawPf : 1;
    return watts / pf;
  }

  const pf = (rawPf > 0 && rawPf <= 1)
    ? rawPf
    : (va > 0 ? Math.min(watts / va, 0.9999) : 0);
  return pf > 0 ? watts / pf : va;
}

/** Get YYYY-MM-DD string from a Date. */
const dayKey = (d: Date): string => d.toISOString().slice(0, 10);

/** Get YYYY-MM string from a Date. */
const monthKey = (d: Date): string => d.toISOString().slice(0, 7);

/** Get 15-minute-aligned window key: "YYYY-MM-DD_HH:mm" */
function demand15Key(d: Date): string {
  const h = d.getHours();
  const m = Math.floor(d.getMinutes() / 15) * 15;
  return `${d.toISOString().slice(0, 10)}_${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/** Neutral current formula: IN = sqrt(IR²+IY²+IB²−IR·IY−IY·IB−IB·IR). Ref PDF §2.3 BO. */
function computeNeutralCurrent(iR: number, iY: number, iB: number): number {
  const val = iR * iR + iY * iY + iB * iB - iR * iY - iY * iB - iB * iR;
  return val > 0 ? Math.sqrt(val) : 0;
}

/** Fire risk classification per PDF §2.3 N. Takes highest severity across all indicators. */
function classifyFireRisk(
  neutralA: number,
  voltageUnbalPct: number,
  currentUnbalPct: number,
): 'NORMAL' | 'WARNING' | 'HIGH' | 'CRITICAL' {
  type Level = 'NORMAL' | 'WARNING' | 'HIGH' | 'CRITICAL';
  const rank: Record<Level, number> = { NORMAL: 0, WARNING: 1, HIGH: 2, CRITICAL: 3 };
  let level: Level = 'NORMAL';

  const up = (l: Level) => { if (rank[l] > rank[level]) level = l; };

  // Neutral current thresholds (PDF §2.3 N)
  if      (neutralA >= 50) up('CRITICAL');
  else if (neutralA >= 30) up('HIGH');
  else if (neutralA >= 15) up('WARNING');

  // Voltage unbalance thresholds
  if      (voltageUnbalPct > 2) up('HIGH');
  else if (voltageUnbalPct > 1) up('WARNING');

  // Current unbalance thresholds
  if      (currentUnbalPct > 50) up('CRITICAL');
  else if (currentUnbalPct > 30) up('HIGH');
  else if (currentUnbalPct > 10) up('WARNING');

  return level;
}

// ─── Per-ToD accumulator helper ───────────────────────────────────────────────

type ByToD = { NORMAL: number; PEAK: number; 'OFF-PEAK': number };

const zeroByToD = (): ByToD => ({ NORMAL: 0, PEAK: 0, 'OFF-PEAK': 0 });

// ─── 15-minute demand window state ───────────────────────────────────────────

interface DemandWindow {
  key:       string;    // demand15Key
  accKwh:    number;    // accumulated kWh (trapezoidal)
  accKvah:   number;    // accumulated kVAh (trapezoidal)
  accKwhByToD:  ByToD;
  accKvahByToD: ByToD;
  durationH: number;    // elapsed time in window (hours)
  prevTs:    Date | null;
  prevWatts: number;
  prevVa:    number;
}

// ─── Engine State ─────────────────────────────────────────────────────────────

interface EngineState {
  // Session
  sessionBaseWh:     number;
  prevPiUptimeHrs:   number;

  // Daily (reset at midnight)
  currentDayKey:     string;
  dayBaseWh:         number;
  dayBaseVah:        number;
  dayAccWhByToD:     ByToD;
  dayAccVahByToD:    ByToD;

  // Billing cycle (reset on 1st of month)
  currentMonthKey:   string;
  billingBaseWh:     number;
  billingBaseVah:    number;
  billingStartTs:    Date | null;
  billingAccWhByToD:  ByToD;
  billingAccVahByToD: ByToD;

  // Max demand (billing cycle)
  maxDemandKw:        number;
  maxDemandKvaByToD:  ByToD;
  maxKva:             number;

  // 99.5th-percentile ceiling on instantaneous VA (spike guard for MaxDemand).
  // Set once per dataset in computeAllDerived; Infinity = no clamp.
  vaCeiling:          number;

  // Active 15-min demand window
  window15: DemandWindow;

  // Previous row
  prev: {
    timestamp: Date;
    whReceived: number;
    vahReceived: number;
    todPeriod: ToDPeriod;
  } | null;
}

function makeWindow15(key: string): DemandWindow {
  return {
    key, accKwh: 0, accKvah: 0,
    accKwhByToD: zeroByToD(), accKvahByToD: zeroByToD(),
    durationH: 0, prevTs: null, prevWatts: 0, prevVa: 0,
  };
}

function initState(firstRow: RawMeterRow & MetaRow): EngineState {
  const now = firstRow.timestamp;
  return {
    sessionBaseWh:    s(firstRow.whReceived),
    prevPiUptimeHrs:  s(firstRow.piUptimeHrs),

    currentDayKey:    dayKey(now),
    dayBaseWh:        s(firstRow.whReceived),
    dayBaseVah:       s(firstRow.vahReceived),
    dayAccWhByToD:    zeroByToD(),
    dayAccVahByToD:   zeroByToD(),

    currentMonthKey:  monthKey(now),
    billingBaseWh:    s(firstRow.whReceived),
    billingBaseVah:   s(firstRow.vahReceived),
    billingStartTs:   now,
    billingAccWhByToD:  zeroByToD(),
    billingAccVahByToD: zeroByToD(),

    maxDemandKw:       0,
    maxDemandKvaByToD: zeroByToD(),
    maxKva:            0,

    vaCeiling:         Infinity,

    window15: makeWindow15(demand15Key(now)),

    prev: null,
  };
}

// ─── State updater ────────────────────────────────────────────────────────────

function advanceState(
  st: EngineState,
  row: RawMeterRow & MetaRow,
  todPeriod: ToDPeriod,
  maxIntegrationGapH: number,
  isKvah: boolean,
): void {
  const ts       = row.timestamp;
  const whNow    = s(row.whReceived);
  const vahNow   = s(row.vahReceived);
  const wattsNow = s(row.wattsTotal);
  // VA_Total = Watts_Total / PF_Avg, clamped to the dataset's 99.5th pct so a
  // single low-PF/sensor spike cannot set a false 15-min Max Demand.
  const vaNow    = Math.min(apparentVa(row, isKvah), st.vaCeiling);

  // ── Pi reboot detection ───────────────────────────────────────────────────
  const piUptime = s(row.piUptimeHrs);
  if (st.prevPiUptimeHrs > 1 && piUptime < st.prevPiUptimeHrs * 0.5) {
    // Uptime dropped significantly → pipeline restarted → reset session
    st.sessionBaseWh = whNow;
  }
  st.prevPiUptimeHrs = piUptime;

  // ── Day change detection (midnight reset) ─────────────────────────────────
  const dk = dayKey(ts);
  if (dk !== st.currentDayKey) {
    st.currentDayKey  = dk;
    st.dayBaseWh      = whNow;
    st.dayBaseVah     = vahNow;
    st.dayAccWhByToD  = zeroByToD();
    st.dayAccVahByToD = zeroByToD();
  }

  // ── Month change detection (billing cycle reset) ──────────────────────────
  const mk = monthKey(ts);
  if (mk !== st.currentMonthKey) {
    st.currentMonthKey    = mk;
    st.billingBaseWh      = whNow;
    st.billingBaseVah     = vahNow;
    st.billingStartTs     = ts;
    st.billingAccWhByToD  = zeroByToD();
    st.billingAccVahByToD = zeroByToD();
    // Reset billing-cycle max demand
    st.maxDemandKw        = 0;
    st.maxDemandKvaByToD  = zeroByToD();
    st.maxKva             = 0;
  }

  // ── Energy deltas (only accumulate plausible positive deltas) ────────────
  if (st.prev) {
    const dtH = (ts.getTime() - st.prev.timestamp.getTime()) / 3_600_000;
    if (dtH > 0 && dtH <= maxIntegrationGapH) {
      const deltaWh  = Math.max(0, whNow  - st.prev.whReceived);
      const deltaVah = Math.max(0, vahNow - st.prev.vahReceived);

      // Accumulate into current ToD bucket (daily)
      st.dayAccWhByToD[todPeriod]  += deltaWh;
      st.dayAccVahByToD[todPeriod] += deltaVah;

      // Accumulate into current ToD bucket (billing cycle)
      st.billingAccWhByToD[todPeriod]  += deltaWh;
      st.billingAccVahByToD[todPeriod] += deltaVah;
    }
  }

  // ── 15-minute demand window ───────────────────────────────────────────────
  const wKey = demand15Key(ts);
  if (wKey !== st.window15.key) {
    // Window just ended — compute average demand and compare against monthly peak
    const w = st.window15;
    if (w.durationH >= (14 / 60)) {
      // Only count windows with ≥14 min of actual data (avoids tiny edge windows)
      const avgKw  = w.durationH > 0 ? w.accKwh  / w.durationH : 0;
      const avgKvaByToD: ByToD = {
        NORMAL:    w.durationH > 0 ? w.accKvahByToD.NORMAL    / w.durationH : 0,
        PEAK:      w.durationH > 0 ? w.accKvahByToD.PEAK      / w.durationH : 0,
        'OFF-PEAK': w.durationH > 0 ? w.accKvahByToD['OFF-PEAK'] / w.durationH : 0,
      };

      if (avgKw > st.maxDemandKw) st.maxDemandKw = avgKw;

      for (const p of ['NORMAL', 'PEAK', 'OFF-PEAK'] as ToDPeriod[]) {
        if (avgKvaByToD[p] > st.maxDemandKvaByToD[p]) {
          st.maxDemandKvaByToD[p] = avgKvaByToD[p];
        }
      }
      // Monthly peak = max across all ToD periods.
      // If this 15-min window's average kVA beats the stored peak, update it.
      const newMaxKva = Math.max(
        st.maxDemandKvaByToD.NORMAL,
        st.maxDemandKvaByToD.PEAK,
        st.maxDemandKvaByToD['OFF-PEAK'],
      );
      if (newMaxKva > st.maxKva) st.maxKva = newMaxKva;
    }
    // Start fresh window
    st.window15 = makeWindow15(wKey);
  }

  // Accumulate trapezoidal contribution into active window
  const w = st.window15;
  if (w.prevTs) {
    const dtH = (ts.getTime() - w.prevTs.getTime()) / 3_600_000;
    if (dtH > 0 && dtH <= 0.5) { // skip gaps > 30 min within a window
      const kwhContrib  = ((s(w.prevWatts) + wattsNow) / 2) / 1000 * dtH;
      const kvahContrib = ((s(w.prevVa)    + vaNow)    / 2) / 1000 * dtH;

      w.accKwh  += kwhContrib;
      w.accKvah += kvahContrib;
      w.accKwhByToD[todPeriod]  += kwhContrib;
      w.accKvahByToD[todPeriod] += kvahContrib;
      w.durationH += dtH;
    }
  }
  w.prevTs    = ts;
  w.prevWatts = wattsNow;
  w.prevVa    = vaNow;

  // ── Update prev ──────────────────────────────────────────────────────────
  st.prev = {
    timestamp:   ts,
    whReceived:  whNow,
    vahReceived: vahNow,
    todPeriod,
  };
}

// ─── Instant derived value computation ────────────────────────────────────────

function computeInstantDerived(
  row: RawMeterRow & MetaRow,
  st: EngineState,
  todPeriod: ToDPeriod,
  contractDemandKva: number,
  tariff: StateTariff,
) {
  const wh  = s(row.whReceived);
  const ts  = row.timestamp;

  const isKvah = tariff.energyUnit === 'kvah';

  // ── Sensor quality ────────────────────────────────────────────────────────
  const isDropout = s(row.vR) < tariff.voltageDropoutV ||
                    s(row.vY) < tariff.voltageDropoutV ||
                    s(row.vB) < tariff.voltageDropoutV;

  // ── ToD rate (placeholder — actual billing rate determined later after MD known) ──
  // Use the state's NORMAL base rate for instantaneous display; billing uses getTariffRates().
  const rateRsKvah = tariff.energyRates.NORMAL;
  const rateRsKwh  = rateRsKvah; // backward-compat alias

  // ── Cleaned PF (PDF §2.3 D–G) ────────────────────────────────────────────
  // BUG FIX: fallback clamp was (0,1) — when Watts>VA the ratio exceeds 1
  // and was clamped to exactly 1.0 (showing as "PF 1.000").  Cap at 0.9999.
  const cleanPF = (raw: number, watts: number, va: number): number => {
    const abs = Math.abs(s(raw));
    if (abs > 0 && abs <= 1) return abs;
    if (s(va) <= 0) return 0;
    return clamp(s(watts) / s(va), 0, 0.9999);
  };

  const pfAvg = cleanPF(row.truePFAvg, row.wattsTotal, row.vaTotal);
  const pfR   = cleanPF(row.truePFR,   row.wattsR,     row.vaR);
  const pfY   = cleanPF(row.truePFY,   row.wattsY,     row.vaY);
  const pfB   = cleanPF(row.truePFB,   row.wattsB,     row.vaB);
  const pfValid = s(row.iTotal) >= 1 && pfAvg > 0; // PDF §2.3 H

  // ── Power (PDF §2.3 J, K) ─────────────────────────────────────────────────
  // kVA from VA_Total = Watts_Total / PF_Avg (spike-clamped), matching the
  // demand-window source so kvaTotal and Max Demand stay consistent.
  const kvaTotal = Math.min(apparentVa(row, isKvah), st.vaCeiling) / 1000;
  const kwTotal  = s(row.wattsTotal)  / 1000;

  // ── Unbalance (PDF §2.3 L, M) ────────────────────────────────────────────
  const vlnAvg = s(row.vlnAvg);
  let voltageUnbalancePct = 0;
  if (vlnAvg > 0) {
    const maxVDev = Math.max(
      Math.abs(s(row.vR) - vlnAvg),
      Math.abs(s(row.vY) - vlnAvg),
      Math.abs(s(row.vB) - vlnAvg),
    );
    voltageUnbalancePct = (maxVDev / vlnAvg) * 100;
  }

  const iAvg = s(row.iTotal);
  let currentUnbalancePct = 0;
  if (iAvg > 0) {
    const maxIDev = Math.max(
      Math.abs(s(row.iR) - iAvg),
      Math.abs(s(row.iY) - iAvg),
      Math.abs(s(row.iB) - iAvg),
    );
    currentUnbalancePct = (maxIDev / iAvg) * 100;
  }

  // ── Neutral current (PDF §2.3 BO) ────────────────────────────────────────
  // Prefer meter-reported value; fall back to vector formula
  const neutralCurrentA = s(row.neutralI) > 0
    ? s(row.neutralI)
    : computeNeutralCurrent(s(row.iR), s(row.iY), s(row.iB));

  // ── Fire risk (PDF §2.3 N) ────────────────────────────────────────────────
  const fireRiskLevel = classifyFireRisk(
    neutralCurrentA, voltageUnbalancePct, currentUnbalancePct,
  );

  // ── Load % (kVA / CD) ────────────────────────────────────────────────────
  const loadPct = contractDemandKva > 0 ? (kvaTotal / contractDemandKva) * 100 : 0;

  // ── Run hours (PDF §2.3 O) ────────────────────────────────────────────────
  // Use Pi uptime as a proxy for pipeline runtime
  const runHours = s(row.piUptimeHrs);

  // ── Session energy (PDF §2.3 Q) ───────────────────────────────────────────
  const sessionEnergyWh = Math.max(0, wh - st.sessionBaseWh);

  // ── Daily energy (PDF §2.3 R–U) ───────────────────────────────────────────
  const dailyEnergyWh       = s(st.dayAccWhByToD.NORMAL) + s(st.dayAccWhByToD.PEAK) + s(st.dayAccWhByToD['OFF-PEAK']);
  const dailyEnergyWhNormal  = s(st.dayAccWhByToD.NORMAL);
  const dailyEnergyWhPeak    = s(st.dayAccWhByToD.PEAK);
  const dailyEnergyWhOffpeak = s(st.dayAccWhByToD['OFF-PEAK']);

  // ── Billing-cycle energy (PDF §2.3 V–AC) ─────────────────────────────────
  const billingWhNormal  = s(st.billingAccWhByToD.NORMAL);
  const billingWhPeak    = s(st.billingAccWhByToD.PEAK);
  const billingWhOffpeak = s(st.billingAccWhByToD['OFF-PEAK']);
  const billingVahNormal  = s(st.billingAccVahByToD.NORMAL);
  const billingVahPeak    = s(st.billingAccVahByToD.PEAK);
  const billingVahOffpeak = s(st.billingAccVahByToD['OFF-PEAK']);

  const energyWhCurrent       = billingWhNormal + billingWhPeak + billingWhOffpeak;
  const energyVahCurrent      = billingVahNormal + billingVahPeak + billingVahOffpeak;

  // ── Cumulative kWh register (backward compat — used by energyDelivered()) ─
  const energyKwh = wh / 1000;

  // ── Derived kVAh (trapezoidal, used when meter register is unavailable) ───
  // Approximation: use billing-cycle VAh accumulator
  const energyVAhDerived = energyVahCurrent / 1000; // kVAh

  // ── Hours elapsed & month fraction ───────────────────────────────────────
  const hoursElapsed  = st.billingStartTs
    ? (ts.getTime() - st.billingStartTs.getTime()) / 3_600_000
    : 0;
  const monthFraction = hoursElapsed / (30 * 24);

  // ── Running weighted avg PF (PDF §2.3 AN) ────────────────────────────────
  // runningAvgPF = ΣkWh_billing / ΣkVAh_billing (valid rows only)
  const runningAvgPF        = energyVahCurrent > 0 ? Math.min(energyWhCurrent  / energyVahCurrent,  0.9999) : 0;
  const runningAvgPFNormal  = billingVahNormal  > 0 ? Math.min(billingWhNormal  / billingVahNormal,  0.9999) : 0;
  const runningAvgPFPeak    = billingVahPeak    > 0 ? Math.min(billingWhPeak    / billingVahPeak,    0.9999) : 0;
  const runningAvgPFOffpeak = billingVahOffpeak > 0 ? Math.min(billingWhOffpeak / billingVahOffpeak, 0.9999) : 0;

  // ── PF rebate / surcharge — TGSPDCL HT uses RKVAH, not a % table ─────────
  // Legacy aliases set to 0 (no WB-style PF rebate table applies).
  const pfRebatePct = 0;
  const pfRebatePctNorm = 0, pfRebatePctPeak = 0, pfRebatePctOffpeak = 0;
  const pfRebateDailyNormal = 0, pfRebateDailyPeak = 0, pfRebateDailyOffpeak = 0;
  const pfRebateDaily = 0;
  const pfRebateCurrNormal = 0, pfRebateCurrPeak = 0, pfRebateCurrOffpeak = 0;
  const pfRebateCurr = 0;

  // ── Load Factor (PDF §2.3 AY) ─────────────────────────────────────────────
  const maxDemandKw = st.maxDemandKw || kwTotal || 1;
  const loadFactorPct = (hoursElapsed > 0 && maxDemandKw > 0)
    ? clamp((energyWhCurrent * 100) / (1000 * maxDemandKw * hoursElapsed), 0, 100)
    : 0;

  // ── No LF rebate in TGSPDCL HT ───────────────────────────────────────────
  const lfRebateRsKwh   = 0;
  const lfRebateDaily   = 0;
  const lfRebateCurrent = 0;

  // ── Energy Charges — per the state's tariff (§3) ─────────────────────────
  // Rate set depends on Max MD vs CD ratio (tiered for TGSPDCL, flat for WBSEDCL).
  const mdKvaNormal  = st.maxDemandKvaByToD.NORMAL;
  const mdKvaPeak    = st.maxDemandKvaByToD.PEAK;
  const mdKvaOffpeak = st.maxDemandKvaByToD['OFF-PEAK'];
  // maxKva = billing-cycle peak of closed 15-min windows only. No kvaTotal fallback.
  const maxKva       = st.maxKva;
  const maxDemandKva = Math.max(s(row.maxDemandMeter), maxKva);

  const rates = tariff.getTariffRates(maxDemandKva, contractDemandKva);

  // Energy charges are computed on the state's billing register:
  //   • kVAh (apparent) for TGSPDCL HT
  //   • kWh  (active)   for WBSEDCL B-IDIT
  const dailyUnitNormal   = (isKvah ? s(st.dayAccVahByToD.NORMAL)    : s(st.dayAccWhByToD.NORMAL))    / 1000;
  const dailyUnitPeak     = (isKvah ? s(st.dayAccVahByToD.PEAK)      : s(st.dayAccWhByToD.PEAK))      / 1000;
  const dailyUnitOffpeak  = (isKvah ? s(st.dayAccVahByToD['OFF-PEAK']) : s(st.dayAccWhByToD['OFF-PEAK'])) / 1000;

  const ecNormalDaily   = dailyUnitNormal  * rates.NORMAL;
  const ecPeakDaily     = dailyUnitPeak    * rates.PEAK;
  const ecOffpeakDaily  = dailyUnitOffpeak * rates['OFF-PEAK'];
  const ecDailyWithoutRebate = ecNormalDaily + ecPeakDaily + ecOffpeakDaily;

  // Billing-cycle energy charges
  const billUnitNormal   = (isKvah ? billingVahNormal  : billingWhNormal)  / 1000;
  const billUnitPeak     = (isKvah ? billingVahPeak    : billingWhPeak)    / 1000;
  const billUnitOffpeak  = (isKvah ? billingVahOffpeak : billingWhOffpeak) / 1000;

  const ecNormalCurrent   = billUnitNormal  * rates.NORMAL;
  const ecPeakCurrent     = billUnitPeak    * rates.PEAK;
  const ecOffpeakCurrent  = billUnitOffpeak * rates['OFF-PEAK'];
  const ecCurrentWithoutRebate = ecNormalCurrent + ecPeakCurrent + ecOffpeakCurrent;

  // Net EC before PF/LF adjustments. The per-state billing engine applies the
  // PF rebate table / LF rebate (WBSEDCL) — the page falls back to it — so the
  // per-row "net" here equals gross (adjustments layered on downstream).
  const dailyNetEC   = ecDailyWithoutRebate;
  const netECCurrent = ecCurrentWithoutRebate;

  // ── Demand Charges — per the state's model (§4) ──────────────────────────
  // WB spec BI:        normalKva = round(max(maxKva, 0.85×CD))        — no CD cap
  // TGSPDCL spec AP:   normalKva = round(max(min(maxKva, CD), 0.80×CD)) — capped at CD
  //
  // BUG FIX (WB): previous code applied max(min(maxKva,CD), minCD) for both states.
  // For WB, when MD > CD, min(maxKva, CD) = CD, giving normalKva = CD — wrong.
  // Spec BI says normalKva = maxKva when MD > CD (no cap at CD for WB).
  const minChargeableKva = contractDemandKva * tariff.minDemandPct;
  const normalKva = !isKvah
    ? Math.round(Math.max(maxKva, minChargeableKva))                          // WB spec BI
    : Math.round(Math.max(Math.min(maxKva, contractDemandKva), minChargeableKva)); // TGSPDCL spec AP
  const addlKva   = Math.round(Math.max(0, maxKva - contractDemandKva));

  const normalDemandCharge = normalKva * tariff.demandChargeRsKva;
  const addlDemandCharge   = addlKva   * tariff.demandPenalRsKva;
  const totalDemandCharge  = normalDemandCharge + addlDemandCharge;

  // ── Max Demand Penalty flag ───────────────────────────────────────────────
  const mdPenaltyFlag: 'NONE' | 'AMBER' =
    maxKva > contractDemandKva ? 'AMBER' : 'NONE';

  // ── Electricity Duty — per the state's model (§4) ────────────────────────
  //   'per-kvah'   → flat Rs per kVAh consumed   (TGSPDCL: 6 paise/kVAh)
  //   'pct-of-bill'→ % of net charge before ED   (WBSEDCL: 12.5%)
  const totalBillingVah = billingVahNormal + billingVahPeak + billingVahOffpeak;
  const netBeforeEd     = netECCurrent + totalDemandCharge;
  const electricityDuty = tariff.electricityDutyModel === 'pct-of-bill'
    ? netBeforeEd * tariff.electricityDutyValue
    : (totalBillingVah / 1000) * tariff.electricityDutyValue;

  // ── Customer / fixed charges — per the state's model ─────────────────────
  // TGSPDCL: customer charge is a flat ₹3,500/month — NOT pro-rated by days elapsed.
  //   It is a fixed monthly line item billed in full from the first day of the cycle.
  //   Billing.ts already handles this correctly for its breakdown; derivedEngine
  //   must match so that the Pi accumulator (totalCost field on the latest row)
  //   reflects the same flat amount and doesn't drift from the billing page.
  // WBSEDCL: customerChargesRsMonth = 0, so pro-ration is moot (result = 0 either way).
  const customerCharges = isKvah
    ? tariff.customerChargesRsMonth                          // TGSPDCL: flat, not pro-rated
    : tariff.customerChargesRsMonth * clamp(monthFraction, 0, 1); // WBSEDCL: keep as-is
  const meterRental     = tariff.meterRentalRsMonth * clamp(monthFraction, 0, 1);

  // ── Sub-total & Net Payable ───────────────────────────────────────────────
  const subTotal     = netBeforeEd;
  const grossPayable = subTotal + electricityDuty + customerCharges + meterRental;
  // Timely Payment Rebate (WBSEDCL); LPSC only applies if paid late.
  const tprRebate    = -(grossPayable * tariff.tprFraction);
  const netPayable   = grossPayable + tprRebate;

  // ── Carbon emission (PDF §2.3 AV) ─────────────────────────────────────────
  const prevWh   = st.prev ? st.prev.whReceived : wh;
  const deltaKwh = Math.max(0, (wh - prevWh) / 1000);
  const carbonKgCo2 = deltaKwh * tariff.carbonKgPerKwh;

  // ── Bill projection (PDF §4 T–U) ──────────────────────────────────────────
  let projectedMonthEndEnergyKwh: number | undefined;
  let projectedMonthEndBillRs: number | undefined;

  const daysElapsed   = hoursElapsed / 24;
  const currentDate   = ts.getDate();
  const daysInMonth   = new Date(ts.getFullYear(), ts.getMonth() + 1, 0).getDate();
  const daysRemaining = Math.max(0, daysInMonth - currentDate);

  // Project on the state's billing register (kVAh for TG, kWh for WB).
  const billingUnitTotal = isKvah
    ? (billingVahNormal + billingVahPeak + billingVahOffpeak)
    : (billingWhNormal  + billingWhPeak  + billingWhOffpeak);

  if (daysElapsed > 0.5 && billingUnitTotal > 0) {
    const dailyAvgUnit = billingUnitTotal / daysElapsed;
    const projTotalUnit = billingUnitTotal + dailyAvgUnit * daysRemaining;

    const splitN = billingUnitTotal > 0 ? (isKvah ? billingVahNormal  : billingWhNormal)  / billingUnitTotal : 1/3;
    const splitP = billingUnitTotal > 0 ? (isKvah ? billingVahPeak    : billingWhPeak)    / billingUnitTotal : 1/3;
    const splitO = billingUnitTotal > 0 ? (isKvah ? billingVahOffpeak : billingWhOffpeak) / billingUnitTotal : 1/3;

    const projEC = (projTotalUnit * splitN / 1000) * rates.NORMAL
                 + (projTotalUnit * splitP / 1000) * rates.PEAK
                 + (projTotalUnit * splitO / 1000) * rates['OFF-PEAK'];

    const projBeforeEd = projEC + totalDemandCharge;
    const projED       = tariff.electricityDutyModel === 'pct-of-bill'
      ? projBeforeEd * tariff.electricityDutyValue
      : (projTotalUnit / 1000) * tariff.electricityDutyValue;
    const projGross    = projBeforeEd + projED + tariff.customerChargesRsMonth + tariff.meterRentalRsMonth;
    projectedMonthEndBillRs       = projGross - projGross * tariff.tprFraction;
    projectedMonthEndEnergyKwh    = energyWhCurrent / 1000
                                  + (energyVahCurrent > 0 ? (energyWhCurrent / energyVahCurrent) : 1)
                                    * (billingUnitTotal > 0 ? dailyAvgUnit : 0) * daysRemaining / 1000;
  }

  // ── Assemble derived row ──────────────────────────────────────────────────
  return {
    // Basic
    todPeriod, rateRsKwh,
    effectiveRate: rateRsKwh * (1 - pfRebatePct / 100),

    // PF
    pfAvg, pfR, pfY, pfB, pfValid,

    // Power
    kvaTotal, kwTotal,
    energyKwh,
    energyVAhDerived,

    // Unbalance + risk
    voltageUnbalancePct,
    currentUnbalancePct,
    neutralCurrentA,
    fireRiskLevel,
    loadPct,
    runHours,
    isDropout,

    // Max demand
    maxDemandKw:        st.maxDemandKw || kwTotal,
    maxDemandKvaNormal: mdKvaNormal,
    maxDemandKvaPeak:   mdKvaPeak,
    maxDemandKvaOffpeak: mdKvaOffpeak,
    maxKva,
    maxDemandKva,

    // Session / daily
    sessionEnergyWh,
    dailyEnergyWh,
    dailyEnergyWhNormal,
    dailyEnergyWhPeak,
    dailyEnergyWhOffpeak,

    // Billing-cycle energy
    energyWhCurrent,
    energyWhNormalCurrent: billingWhNormal,
    energyWhPeakCurrent:   billingWhPeak,
    energyWhOffpeakCurrent: billingWhOffpeak,
    energyVahCurrent,
    energyVahNormalCurrent:  billingVahNormal,
    energyVahPeakCurrent:    billingVahPeak,
    energyVahOffpeakCurrent: billingVahOffpeak,

    // Daily charges
    ecNormalDaily, ecPeakDaily, ecOffpeakDaily, ecDailyWithoutRebate,

    // Monthly charges
    ecNormalCurrent, ecPeakCurrent, ecOffpeakCurrent, ecCurrentWithoutRebate,

    // Running avg PF
    runningAvgPF, runningAvgPFNormal, runningAvgPFPeak, runningAvgPFOffpeak,

    // PF rebate
    pfRebatePct, pfRebatePctNorm, pfRebatePctPeak, pfRebatePctOffpeak,
    pfRebateDailyNormal, pfRebateDailyPeak, pfRebateDailyOffpeak, pfRebateDaily,
    pfRebateCurrNormal, pfRebateCurrPeak, pfRebateCurrOffpeak, pfRebateCurr,

    // LF
    loadFactorPct, lfRebateRsKwh, lfRebateDaily, lfRebateCurrent,

    // Net charges
    dailyNetEC, netECCurrent,

    // Demand
    mdPenaltyFlag, normalKva, addlKva,
    normalDemandCharge, addlDemandCharge, totalDemandCharge,

    // Bill (TGSPDCL)
    electricityDuty, customerCharges,
    meterRental, tprRebate, netPayable,
    projectedMonthEndEnergyKwh, projectedMonthEndBillRs,

    // Carbon
    carbonKgCo2,

    // Backward-compat aliases
    dailyCostRs:         dailyNetEC,
    // BUG-1 FIX: totalCost must hold the energy-only MTD charge (EC without PF/LF rebates)
    // so that Billings.tsx can safely add demand charges and ED without double-counting.
    // grossPayable (= EC + DC + ED + rental) is already exposed as netPayable / via meterRental.
    totalCost:           netECCurrent,
    totalEnergyCharge:   ecCurrentWithoutRebate,
    energyChargeNormal:  ecNormalCurrent,
    energyChargePeak:    ecPeakCurrent,
    energyChargeOffpeak: ecOffpeakCurrent,
    pfRebateCost:        0,
    lfRebateCost:        0,
    dailyEnergyCostRs:   ecDailyWithoutRebate,
    dailyPfRebateRs:     0,
    dailyPfPenaltyRs:    0,
    dailyLfRebateRs:     0,
  };
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Compute all derived variables for every row in the dataset.
 *
 * @param rawRows  Array of joined (RawMeterRow & MetaRow) objects, any order.
 * @param contractDemandKva  Installation contract demand. Defaults to CONTRACT_DEMAND_KVA.
 * @returns TelemetryRow[] sorted chronologically with all 67+ derived fields.
 */
/**
 * Compute all derived variables for every row in the dataset.
 *
 * @param rawRows  Array of joined (RawMeterRow & MetaRow) objects, any order.
 * @param contractDemandKva  Installation contract demand. Defaults to the tariff's default CD.
 * @param tariff   The state tariff config (resolved from the meter's stateCode).
 *                 Drives ToD bucketing, energy register/rates, demand, ED, fixed charges.
 * @returns TelemetryRow[] sorted chronologically with all 67+ derived fields.
 */
export function computeAllDerived(
  rawRows: (RawMeterRow & MetaRow)[],
  contractDemandKva?: number,
  tariff: StateTariff = getTariff('west-bengal'),
): TelemetryRow[] {
  if (!rawRows.length) return [];

  const cd = contractDemandKva ?? tariff.defaultContractDemandKva;

  // Sort chronologically
  const sorted = [...rawRows].sort(
    (a, b) => a.timestamp.getTime() - b.timestamp.getTime(),
  );

  const state = initState(sorted[0]);
  const result: TelemetryRow[] = [];

  // WB-only: apparentVa() drops the raw VA_Total fallback for WBSEDCL
  // (energyUnit !== 'kvah'). Telangana (kvah tariff) is unchanged.
  const isKvah = tariff.energyUnit === 'kvah';

  // Spike guard for Max Demand: clamp instantaneous VA_Total (= W/PF) to its
  // 99.5th percentile across the dataset. Needs ≥10 samples to be meaningful;
  // otherwise left unclamped. Mirrors sanitiseWindow()'s 99.5 ceiling, but here
  // it protects the billing-path Max Demand, which sanitiseWindow never touches.
  const vaSamples = sorted.map((r) => apparentVa(r, isKvah)).filter((v) => v > 0);
  state.vaCeiling = vaSamples.length >= 10 ? percentile(vaSamples, 99.5) : Infinity;

  for (const row of sorted) {
    // Determine ToD for this row using the STATE's window definition
    const todPeriod = tariff.todPeriodFor(row.timestamp.getHours());

    // Advance accumulators BEFORE computing derived snapshot
    advanceState(state, row, todPeriod, tariff.maxIntegrationGapH, isKvah);

    // Compute instant derived snapshot
    const derived = computeInstantDerived(row, state, todPeriod, cd, tariff);

    // Merge: raw aliases + raw fields + meta + derived
    const full: TelemetryRow = {
      ...row,
      // Backward-compat aliases on the raw side
      currentTotal: s(row.iTotal),
      currentR:     s(row.iR),
      currentY:     s(row.iY),
      currentB:     s(row.iB),
      pfAvgRaw:     s(row.truePFAvg),
      pfRRaw:       s(row.truePFR),
      pfYRaw:       s(row.truePFY),
      pfBRaw:       s(row.truePFB),
      energyWh:     s(row.whReceived),
      energyVAh:    s(row.vahReceived),
      // Derived
      ...derived,
    };

    result.push(full);
  }

  return result;
}